import React, { useEffect, useMemo, useRef, useState } from "react"
import { graphql } from "gatsby"

import CmsPage from "@utils/cmsPage"
import { trackCtaClick } from "@utils/trackEvent"
import {
  pickCollectionNode,
  resolveFaqCollection,
  resolveProofCollection,
} from "@utils/cmsCollections"
import industrySubpageMobileStyles from "@utils/industrySubpageMobileStyles"
import { getPrototypePageSlice } from "@utils/prototypeSlices"
import { nonprofitsSeoSections } from "../data/seoStorySections"

const prototypeSlice = getPrototypePageSlice("nonprofits")
const nonprofitsExtraStyles = `${prototypeSlice.extraStyles}${industrySubpageMobileStyles}

@media (max-width: 960px) {
  .cms-page .hero-f .trust-badges {
    align-items: center;
    gap: 8px;
  }

  .cms-page .hero-f .trust-badge {
    width: auto;
    max-width: 100%;
    gap: 7px;
    padding: 6px 10px;
    font-size: 11.5px;
    line-height: 1.35;
  }

  .cms-page .hero-f .trust-badge svg {
    width: 16px;
    height: 16px;
  }

  .cms-page .hero-f .trust-badge b {
    font-size: 11.5px;
  }

  .cms-page .hero-f .lift-callout {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 5px;
    justify-content: center;
    line-height: 1.35;
    margin-left: auto;
    margin-right: auto;
    max-width: 320px;
    text-align: center;
  }

  .cms-page .hero-f .lift-callout svg {
    margin-bottom: 2px;
  }

  .cms-page .hero-f .lift-callout b {
    display: block;
    font-size: 1.15em;
    line-height: 1.1;
  }

  .cms-page .hero-f .trust-stats {
    gap: 12px;
  }

  .cms-page .hero-f .trust-stat {
    position: relative;
  }

  .cms-page .hero-f .trust-stat + .trust-stat::before,
  .cms-page .hero-f .trust-stat::after {
    display: none !important;
  }

}
`

const nonprofitHeroSlides = [
  {
    url: "harbor-watershed.org/give",
    logo: "linear-gradient(135deg,#0ea5e9,#0369a1)",
    org: "Harbor Watershed Alliance",
    kind: "Make a gift",
    email: "sarah@example.com",
    name: "Sarah Chen",
    amount: "$30",
    button: "Give $30 / month",
  },
  {
    url: "newhopecommunity.org/donate",
    logo: "linear-gradient(135deg,#14b8a6,#0f766e)",
    org: "New Hope Community Church",
    kind: "Support the mission",
    email: "marcus@example.com",
    name: "Marcus Lee",
    amount: "$50",
    button: "Give $50 / month",
  },
  {
    url: "riverkeeper.org/support",
    logo: "linear-gradient(135deg,#f59e0b,#db2777)",
    org: "Riverkeeper Alliance",
    kind: "Protect the river",
    email: "maya@example.com",
    name: "Maya Torres",
    amount: "$100",
    button: "Give $100 / month",
  },
]

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const nonprofitFallbackTestimonials = [
  {
    quote:
      "We tripled our monthly giving program in six months. Recurring gifts are now easier for donors and easier for our team to manage.",
    customerName: "Amara Johnson",
    customerInitials: "AJ",
    customerRole: "Executive Director, Harbor Watershed Alliance",
    industry: "Nonprofits",
    brandName: "Harbor Watershed Alliance",
    metaOne: "Cause: Coastal conservation",
    metaTwo: "Size: 9 staff · New England",
    metaThree: "On MoonClerk since: 2022",
    statOneValue: "3.1×",
    statOneLabel: "Monthly giving lift",
    statTwoValue: "48%",
    statTwoLabel: "12-mo retention",
    statThreeValue: "14 hrs",
    statThreeLabel: "Saved per month on admin",
  },
  {
    quote:
      "Our board kept asking for a better donation page. I had one live Monday morning - no developer, no agency, no meeting.",
    customerName: "Rev. Marcus Lee",
    customerInitials: "ML",
    customerRole: "New Hope Community Church",
    industry: "Nonprofits",
  },
  {
    quote:
      "Our donation form finally looks like the rest of our site. Donors notice - we get fewer 'is this legit?' emails and more recurring gifts.",
    customerName: "Marcus L.",
    customerInitials: "ML",
    customerRole: "Communications Director, Riverkeeper Alliance",
    industry: "Nonprofits",
  },
]

const nonprofitFallbackFaqItems = [
  // Handoff v3.7 §03 — church-giving item added on Directive's audit advice.
  {
    question: "Does MoonClerk work as a church online giving platform?",
    answer:
      "Yes. Churches use MoonClerk for weekly and monthly giving, building funds, and one-time gifts — a branded giving page per fund, recurring gifts on Stripe, and donor self-serve for amount and card updates. See <a href=\"/pricing/\">pricing</a> for what a giving page costs.",
  },
  {
    question: "Do you offer nonprofit pricing?",
    answer:
      "MoonClerk plans are the same flat monthly price for every org, and payments run on Stripe's standard nonprofit rate (2.2% + 30¢) for verified 501(c)(3)s. No paperwork with us - Stripe verifies your status directly.",
  },
  {
    question: "Can we match our nonprofit's branding on the donation form?",
    answer:
      "Yes. Every donation form can be branded to match your nonprofit's voice perfectly - logo, colors, typography, and copy. No design tools, no developer, no theme files. Edit it in MoonClerk and your form looks like the rest of your site.",
  },
  {
    question: "Can donors change or pause their gift?",
    answer:
      "Recurring donors can manage common payment details through payer-facing tools, including card updates and payment history. Your team can still control the policies and support flow around recurring gifts.",
  },
  {
    question: "What about matching gifts?",
    answer:
      "MoonClerk integrates with Double the Donation via our Zapier connection, so the matching-gift lookup shows up right on your donation page. You can also use your own matching-gift widget alongside MoonClerk.",
  },
  {
    question: "Do you process peer-to-peer fundraising?",
    answer:
      "MoonClerk is focused on your primary giving page. For event-based peer-to-peer campaigns, most of our nonprofits pair MoonClerk with a dedicated P2P tool (Classy, Funraise, GiveButter) - your recurring donors stay with us and your event donors go through the other platform.",
  },
]

const ensureSection = (sections, section) =>
  sections.some(item => item?.sectionKey === section.sectionKey) ? sections : [...sections, section]

const nonprofitSectionOrder = {
  nonprofits_test_band: 10,
  nonprofits_recurring: 20,
  nonprofits_seo_how: 25,
  nonprofits_branded_forms: 30,
  nonprofits_placement: 40,
  nonprofits_seo_donor: 45,
  nonprofits_impact_strip: 50,
  nonprofits_story_card: 60,
  nonprofits_faq: 70,
  nonprofits_final_cta: 80,
}

const NonprofitsHero = () => {
  const mockRef = useRef(null)
  const activeRef = useRef(true)
  const visibleRef = useRef(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [fieldValues, setFieldValues] = useState({
    email: nonprofitHeroSlides[0].email,
    name: nonprofitHeroSlides[0].name,
    card: "•••• •••• •••• 4242",
  })
  const [activeField, setActiveField] = useState(null)
  const [payPulse, setPayPulse] = useState(false)
  const [stampVisible, setStampVisible] = useState(false)
  const active = nonprofitHeroSlides[activeIndex]

  useEffect(() => {
    activeRef.current = true
    visibleRef.current = true

    if ("IntersectionObserver" in window && mockRef.current) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            visibleRef.current = entry.isIntersecting
          })
        },
        { threshold: 0.2 }
      )
      observer.observe(mockRef.current)

      return () => {
        activeRef.current = false
        observer.disconnect()
      }
    }

    return () => {
      activeRef.current = false
    }
  }, [])

  useEffect(() => {
    const typeInto = async (field, fullValue) => {
      setActiveField(field)
      setFieldValues(values => ({ ...values, [field]: "" }))

      const chunk = Math.max(1, Math.ceil(fullValue.length / 14))
      for (let position = 0; position < fullValue.length && activeRef.current; position += chunk) {
        const nextValue = fullValue.slice(0, Math.min(fullValue.length, position + chunk))
        setFieldValues(values => ({ ...values, [field]: nextValue }))
        await wait(45)
      }

      setActiveField(null)
    }

    const runCycle = async (slide, index) => {
      setPayPulse(false)
      setStampVisible(false)
      setActiveIndex(index)
      setFieldValues({ email: "", name: "", card: "" })
      await wait(450)
      await typeInto("email", slide.email)
      await wait(100)
      await typeInto("name", slide.name)
      await wait(100)
      await typeInto("card", "•••• •••• •••• 4242")
      await wait(280)
      setPayPulse(true)
      await wait(900)
      setStampVisible(true)
      await wait(1500)
    }

    const loop = async () => {
      let index = 0

      while (activeRef.current) {
        while (activeRef.current && !visibleRef.current) {
          await wait(220)
        }

        await runCycle(nonprofitHeroSlides[index], index)
        index = (index + 1) % nonprofitHeroSlides.length
        await wait(600)
      }
    }

    loop()

    return () => {
      activeRef.current = false
    }
  }, [])

  return (
    <section className="hero-f">
      <div className="hero-f-inner">
        <div className="hero-f-left">
          <div className="eyebrow">For nonprofits</div>
          {/* Handoff v3.7 §03 — Directive H1, verbatim. */}
          <h1>
            Accept Stripe recurring payments with <em>donation forms for nonprofits.</em>
          </h1>
          <p className="lede">
            Launch a branded giving page in minutes. Recurring-first by design,
            with recurring gifts and donor-friendly payment updates - no developer required.
          </p>

          <div className="lift-callout">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
            <span>Recurring gifts raise</span>
            <b>42% more</b>
            <span>than one-time, on average</span>
          </div>

          <div className="cta-row" style={{ display: "flex", gap: 12, marginBottom: 28 }}>
            <a
              href="https://app.moonclerk.com/signup?try=1"
              className="btn btn-accent btn-lg"
              onClick={() =>
                trackCtaClick({
                  label: "Start a donation page",
                  location: "nonprofits_hero",
                  destination: "https://app.moonclerk.com/signup?try=1",
                })
              }
            >
              Start a donation page
            </a>
          </div>

          <div className="trust-card">
            <div className="trust-stats">
              <div className="trust-stat">
                <div className="trust-stat-num">1,200+</div>
                <div className="trust-stat-label">Nonprofits</div>
              </div>
              <div className="trust-stat">
                <div className="trust-stat-num">$412M+</div>
                <div className="trust-stat-label">Donations processed</div>
              </div>
              <div className="trust-stat">
                <div className="trust-stat-num">68%</div>
                <div className="trust-stat-label">Recurring retention at 12mo</div>
              </div>
            </div>
            <div className="trust-badges">
              <div className="trust-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: "var(--teal-dk)" }}>
                  <path d="M12 2 4 6v6c0 4.4 3.4 8.4 8 10 4.6-1.6 8-5.6 8-10V6l-8-4Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span>
                  <b>Recurring-first</b> donation pages
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-f-right">
          <div className="form-frame donate-mock" ref={mockRef}>
            <div className="form-frame-chrome">
              <span className="d" />
              <span className="d" />
              <span className="d" />
              <span className="u">{active.url}</span>
            </div>
            <div className="form-frame-body">
              <div className="form-brand-row">
                <div className="form-brand-logo" style={{ background: active.logo }} />
                <div className="form-brand-meta">
                  <div className="form-org">{active.org}</div>
                  <div className="form-kind">{active.kind}</div>
                </div>
              </div>

              <div className="donate-freq">
                <button className="is-mock-control" type="button" aria-disabled="true" tabIndex={-1}>One-time</button>
                <button className="on is-mock-control" type="button" aria-disabled="true" tabIndex={-1}>Monthly</button>
                <button className="is-mock-control" type="button" aria-disabled="true" tabIndex={-1}>Yearly</button>
              </div>

              <div className="donate-amounts">
                {["$15", "$30", "$50", "$100", "$250", "$500"].map(amount => (
                  <div className={`donate-amt${amount === active.amount ? " selected" : ""}`} key={amount}>
                    {amount}
                  </div>
                ))}
              </div>
              <div className="donate-custom">or enter a custom amount...</div>

              <div className="co-field">
                <label htmlFor="nonprofits-hero-email">Email</label>
                <input
                  id="nonprofits-hero-email"
                  className={`co-input${activeField === "email" ? " typing" : ""}`}
                  value={fieldValues.email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
              <div className="co-field">
                <label htmlFor="nonprofits-hero-name">Full name</label>
                <input
                  id="nonprofits-hero-name"
                  className={`co-input${activeField === "name" ? " typing" : ""}`}
                  value={fieldValues.name}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
              <div className="co-field">
                <label htmlFor="nonprofits-hero-card">Card</label>
                <input
                  id="nonprofits-hero-card"
                  className={`co-input focus${activeField === "card" ? " typing" : ""}`}
                  value={fieldValues.card}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>

              <button
                className={`co-pay is-mock-control${payPulse ? " pulse" : ""}`}
                type="button"
                style={{ background: "var(--teal)" }}
                aria-disabled="true"
                tabIndex={-1}
              >
                {active.button}
              </button>
              <div style={{ textAlign: "center", fontSize: 10, color: "var(--ink-3)", marginTop: 10, letterSpacing: ".06em" }}>
                SECURE PAYMENT · BRANDED CONFIRMATION
              </div>
              <div className={`donate-stamp${stampVisible ? " show" : ""}`}>Gift received ✓</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const NonprofitsPage = ({ data }) => {
  const page = data.allDatoCmsMcV2PageVertical.nodes[0] || {}
  const sectionCollection =
    page.sectionCollection ||
    pickCollectionNode(
      data.allDatoCmsMcV2SectionCollection.nodes,
      null,
      "nonprofits-sections"
    )
  const allSections = sectionCollection.contentSections || []
  const heroMetaSection =
    allSections.find(section => section?.sectionKey === "nonprofits_hero_meta") || null
  const contentSections = allSections.filter(section => section?.sectionKey !== "nonprofits_hero_meta")
  const proofCollection =
    page.proofCollection ||
    resolveProofCollection(data.allDatoCmsMcV2ProofCollection.nodes, null, "nonprofits-proof")
  const faqCollection =
    page.faqCollection ||
    resolveFaqCollection(data.allDatoCmsMcV2FaqCollection.nodes, null, "nonprofits-faq")
  const faqItems = faqCollection.faqItems?.length
    ? faqCollection.faqItems
    : nonprofitFallbackFaqItems
  const testimonials = proofCollection.testimonials?.length
    ? proofCollection.testimonials.map(item =>
        /Harbor Watershed Alliance/i.test(`${item.customerRole || ""} ${item.brandName || ""}`)
          ? { ...item, statTwoValue: "48%" }
          : item
      )
    : nonprofitFallbackTestimonials
  const managedContentSections = useMemo(() => {
    let sections = contentSections

    sections = ensureSection(sections, {
      sectionKey: "nonprofits_test_band",
      componentType: "test_band",
      hidden: false,
      headingHtml: "Trusted by 1,200+ nonprofits raising for the causes they lead",
    })
    sections = ensureSection(sections, {
      sectionKey: "nonprofits_story_card",
      componentType: "story_card",
      hidden: false,
      eyebrow: "Customer story",
    })
    sections = ensureSection(sections, {
      sectionKey: "nonprofits_faq",
      componentType: "faq_list",
      hidden: false,
      eyebrow: "FAQ",
      headingHtml: "Donation platform questions for <em>nonprofits and churches.</em>",
    })
    sections = ensureSection(sections, {
      sectionKey: "nonprofits_final_cta",
      componentType: "cta",
      hidden: false,
      headingHtml: "Launch Stripe recurring payments in <em>under 5 minutes.</em>",
      summary: "Explore MoonClerk free. No setup fee. Stripe's 2.2% nonprofit rate on every gift.",
      primaryCtaLabel: "Explore for free",
      primaryCtaHref: "https://app.moonclerk.com/signup?try=1",
    })
    // Handoff v3.7 §04 — two SEO story sections at Directive's insert points.
    nonprofitsSeoSections.forEach(seoSection => {
      sections = ensureSection(sections, seoSection)
    })


    return sections
      .slice()
      .sort((a, b) => {
        const aOrder = nonprofitSectionOrder[a?.sectionKey] || 999
        const bOrder = nonprofitSectionOrder[b?.sectionKey] || 999
        return aOrder - bOrder
      })
  }, [contentSections])

  return (
    <CmsPage
      slug={page.slug}
      seo={page.seo}
      eyebrow={page.eyebrow}
      heading="Accept Stripe recurring payments with donation forms for nonprofits."
      summary="Launch a branded giving page in minutes. Recurring-first by design, with donor-friendly payment updates - no developer required."
      body={page.body}
      bodyHeading={page.bodyHeading || "For Nonprofits"}
      heroMediaType={page.heroMediaType}
      heroImageUrl={page.heroImageUrl}
      heroImageAlt={page.heroImageAlt}
      heroVideoUrl={page.heroVideoUrl}
      heroVideoPosterUrl={page.heroVideoPosterUrl}
      heroSlot={<NonprofitsHero />}
      heroMetaSection={heroMetaSection}
      contentSections={managedContentSections}
      testimonials={testimonials}
      faqItems={faqItems}
      contentHtml={page.contentHtml}
      fallbackContentHtml={prototypeSlice.contentHtml}
      extraStyles={nonprofitsExtraStyles}
      extraHeadLinks={prototypeSlice.extraHeadLinks}
      emptyMessage="Create or seed the nonprofits record in mc_v2_page_vertical."
    />
  )
}

export default NonprofitsPage

export const query = graphql`
  query NonprofitsCmsPageQuery {
    allDatoCmsMcV2PageVertical(filter: { slug: { eq: "nonprofits" } }, limit: 1) {
      nodes {
        seo {
          ...CmsSeoFields
        }
        slug
        eyebrow
        heading
        summary
        body
        # bodyHeading  # TODO(T-014 add the Dato field, T-015 then uncomment): blocked on DatoCMS schema
        heroMediaType
        heroImageUrl
        heroImageAlt
        heroVideoUrl
        heroVideoPosterUrl
        contentHtml
        sectionCollection {
          contentSections {
            ...CmsHtmlSectionFields
          }
        }
        proofCollection {
          sourceCollection {
            key
          }
          selectedProfileEntries {
            quote
            name
            initials
            role
            industry
            stat
            logoSvg
            gradient
          }
          selectedTestimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
          testimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
        }
        faqCollection {
          sourceCollection {
            key
          }
          selectedFaqEntries {
            question
            answer
          }
        }
      }
    }
    allDatoCmsMcV2SectionCollection {
      nodes {
        key
        contentSections {
          ...CmsHtmlSectionFields
        }
      }
    }
    allDatoCmsMcV2ProofCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedProfileEntries {
          quote
          name
          initials
          role
          industry
          stat
          logoSvg
          gradient
        }
        selectedTestimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
        testimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
      }
    }
    allDatoCmsMcV2FaqCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedFaqEntries {
          question
          answer
        }
      }
    }
  }
`
